import type { QueryClient } from '@tanstack/react-query';
import type { FamilyTree } from '../../app/api/generated/client';
import { queryKeys } from '../api/query-keys';

interface FamilyTreeSnapshot {
    relationshipFingerprints: string[];
    partnerCount: number;
    connectorCount: number;
}

function relationshipFingerprint(r: {
    relationshipUuid?: string;
    source?: string;
    target?: string;
    relationshipType?: string;
    justification?: string | null;
}): string {
    return [r.relationshipUuid ?? '', r.source ?? '', r.target ?? '', r.relationshipType ?? '', r.justification ?? ''].join('|');
}

function getFamilyTreeSignature(data?: FamilyTree): FamilyTreeSnapshot | null {
    if (!data || typeof data !== 'object') return null;
    const tree = data as {
        relationships?: Array<{ relationshipUuid?: string; source?: string; target?: string; relationshipType?: string; justification?: string | null }>;
        partnerNodes?: unknown[];
        connectorNodes?: unknown[];
    };
    const relationshipFingerprints = (tree.relationships ?? []).map(relationshipFingerprint).sort();

    return {
        relationshipFingerprints,
        partnerCount: tree.partnerNodes?.length ?? 0,
        connectorCount: tree.connectorNodes?.length ?? 0,
    };
}

function signaturesDiffer(before: FamilyTreeSnapshot | null, after: FamilyTreeSnapshot | null): boolean {
    if (before === null || after === null) return before !== after;
    if (before.relationshipFingerprints.length !== after.relationshipFingerprints.length) return true;
    if (before.partnerCount !== after.partnerCount || before.connectorCount !== after.connectorCount) return true;

    return before.relationshipFingerprints.some((fp, i) => fp !== after.relationshipFingerprints[i]);
}

const INITIAL_DELAY_MS = 600;
const INITIAL_INTERVAL_MS = 800;
const BACKOFF_MULTIPLIER = 2;
const MAX_INTERVAL_MS = 3500;
const MAX_ATTEMPTS = 10;

/**
 * Polls the family tree until data has changed (PCM propagation) or max attempts reached.
 * Uses exponential backoff between refetches to avoid hammering the server and to give
 * PCM time to apply changes (eventual consistency).
 */
export function refetchFamilyTreeAfterRelationChange(queryClient: QueryClient, partnerUuid: string): void {
    const familyTreeKey = queryKeys.familyTree(partnerUuid);
    const snapshotBefore = getFamilyTreeSignature(queryClient.getQueryData<FamilyTree>(familyTreeKey));

    let attempt = 0;
    let nextDelayMs = INITIAL_INTERVAL_MS;

    const poll = async () => {
        if (attempt >= MAX_ATTEMPTS) return;

        attempt += 1;
        await queryClient.refetchQueries({ queryKey: familyTreeKey });
        const snapshotAfter = getFamilyTreeSignature(queryClient.getQueryData<FamilyTree>(familyTreeKey));

        if (signaturesDiffer(snapshotBefore, snapshotAfter)) return;

        const delay = Math.min(nextDelayMs, MAX_INTERVAL_MS);
        nextDelayMs *= BACKOFF_MULTIPLIER;
        window.setTimeout(poll, delay);
    };

    window.setTimeout(poll, INITIAL_DELAY_MS);
}
